import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Globe, MessageCircle, Clock3, Eye, X } from 'lucide-react';
import { toast } from 'sonner';
import { useT } from '../../i18n';
import { authFetch } from '../../lib/auth';
import { useOs2, useRangeLabel, PageHead, Card, StageBadge, Th, Td, fmtDateTime, InfoTip, Fade, LoadingState, STAGE_GRADIENTS, Pager } from './os2ui';

const REC_BUCKETS = (isEs) => [
  { key: 'd7', label: isEs ? 'Activos · 7 días' : 'Active · 7 days', color: '#34D399', max: 7 },
  { key: 'd30', label: isEs ? '8–30 días' : '8–30 days', color: '#38BDF8', max: 30 },
  { key: 'd90', label: isEs ? '31–90 días' : '31–90 days', color: '#FBBF24', max: 90 },
  { key: 'cold', label: isEs ? 'Dormidos +90 días' : 'Dormant +90 days', color: '#94A3B8', max: Infinity },
];

function AudienceStory({ people, pulse, isEs }) {
  const total = Math.max(1, people.length);
  const scans = pulse?.scans;
  const steps = [
    ['scan_completed', isEs ? 'Hicieron el scan' : 'Completed the scan', STAGE_GRADIENTS[1]],
    ['report_downloaded', isEs ? 'Descargaron su reporte' : 'Downloaded their report', STAGE_GRADIENTS[2]],
    ['email_opened', isEs ? 'Abrieron nuestros emails' : 'Opened our emails', STAGE_GRADIENTS[3]],
    ['email_clicked', isEs ? 'Hicieron clic en un CTA' : 'Clicked a CTA', STAGE_GRADIENTS[5]],
  ].map(([k, l, g]) => ({ k, l, g, n: people.filter((p) => p[k]).length }));
  const buckets = REC_BUCKETS(isEs).map((b) => ({ ...b, n: 0 }));
  const now = Date.now();
  people.forEach((p) => {
    const days = p.last_activity ? (now - new Date(p.last_activity).getTime()) / 86400000 : Infinity;
    (buckets.find((b) => days <= b.max) || buckets[3]).n += 1;
  });
  const customers = people.filter((p) => p.stage === 'CUSTOMER').length;
  const active30 = buckets[0].n + buckets[1].n;
  return (
    <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card className="p-5 relative overflow-hidden" data-testid="customers-story">
        <span className="absolute inset-x-0 top-0 h-[3px]" style={{ background: 'linear-gradient(90deg,#0369A1,#38BDF8,transparent)' }} aria-hidden />
        <p className="text-[11px] tracking-wider uppercase text-gray-400 font-bold mb-3">{isEs ? 'Historia de la audiencia' : 'Audience story'}</p>
        {scans && scans.total > 0 && (
          <div className="mb-3 flex items-center justify-between gap-3 rounded-xl bg-sky-50 border border-sky-100 px-3 py-2" data-testid="customers-scans-real">
            <span className="text-[12px] text-sky-800">
              <b className="tabular-nums">{scans.total}</b> {isEs ? 'scans Index AI reales en el periodo' : 'real Index AI scans this period'}
              {scans.anonymous > 0 && <span className="text-sky-500"> · {scans.anonymous} {isEs ? 'anónimos (sin email, no rastreables abajo)' : 'anonymous (no email, not trackable below)'}</span>}
            </span>
            <b className="tabular-nums text-[13px] text-sky-700 shrink-0">{scans.with_email} {isEs ? 'con email' : 'with email'}</b>
          </div>
        )}
        <div className="space-y-2.5">
          {steps.map((s, i) => (
            <div key={s.k}>
              <div className="flex items-center justify-between text-[12.5px]">
                <span className="text-gray-600">{s.l}</span>
                <span className="tabular-nums font-bold text-gray-900">{s.n} <span className="text-gray-400 font-normal">· {Math.round((s.n / total) * 100)}%</span></span>
              </div>
              <div className="mt-1 h-2 rounded-full bg-gray-100 overflow-hidden">
                <div className="h-full rounded-full transition-[width] duration-700" style={{ width: `${Math.max(3, (s.n / total) * 100)}%`, background: s.g, transitionDelay: `${i * 120}ms` }} />
              </div>
            </div>
          ))}
        </div>
        <p className="mt-3 text-[11px] text-gray-400">{isEs ? `De ${people.length} personas identificadas — cada barra es un paso hacia la compra.` : `Of ${people.length} identified people — each bar is one step closer to buying.`}</p>
      </Card>
      <Card className="p-5 relative overflow-hidden" data-testid="customers-retention">
        <span className="absolute inset-x-0 top-0 h-[3px]" style={{ background: 'linear-gradient(90deg,#047857,#34D399,transparent)' }} aria-hidden />
        <p className="text-[11px] tracking-wider uppercase text-gray-400 font-bold mb-3">{isEs ? 'Retención · frescura de la relación' : 'Retention · relationship freshness'}</p>
        <div className="flex h-3 rounded-full overflow-hidden bg-gray-100">
          {buckets.map((b) => b.n > 0 && (
            <div key={b.key} title={`${b.label}: ${b.n}`} className="h-full transition-[width] duration-700"
              style={{ width: `${(b.n / total) * 100}%`, background: b.color }} />
          ))}
        </div>
        <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5">
          {buckets.map((b) => (
            <p key={b.key} className="flex items-center gap-1.5 text-[12px] text-gray-600">
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: b.color }} /> {b.label}
              <b className="ml-auto tabular-nums text-gray-900">{b.n}</b>
            </p>
          ))}
        </div>
        <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-5 text-[12.5px] text-gray-600">
          <span>{isEs ? 'Clientes activos' : 'Active customers'}: <b className="text-emerald-600 tabular-nums">{customers}</b></span>
          <span>{isEs ? 'Relación viva (≤30d)' : 'Warm relationships (≤30d)'}: <b className="text-gray-900 tabular-nums">{Math.round((active30 / total) * 100)}%</b></span>
        </div>
      </Card>
    </div>
  );
}

const aiIntent = (p, isEs) => {
  if (p.stage === 'CUSTOMER') return [isEs ? 'Cliente activo — cuidar y hacer upsell.' : 'Active customer — nurture & upsell.', 'emerald'];
  if (p.stage === 'CALL_BOOKED') return [isEs ? 'Intención de compra muy alta — llamada agendada.' : 'Very high buying intent — call booked.', 'violet'];
  if ((p.lead_score || 0) >= 60 || (p.email_clicked && p.report_downloaded)) return [isEs ? 'Alta intención de compra.' : 'High buying intent.', 'orange'];
  if (p.scan_completed && p.report_downloaded) return [isEs ? 'Evaluando activamente — descargó su reporte.' : 'Actively evaluating — downloaded their report.', 'sky'];
  if (p.email_opened || p.email_clicked) return [isEs ? 'Interés creciente — responde a los emails.' : 'Growing interest — engaging with emails.', 'sky'];
  return [isEs ? 'Interés temprano — aún explorando.' : 'Early interest — still exploring.', 'gray'];
};

const INTENT_STYLE = {
  emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
  violet: 'bg-violet-50 border-violet-200 text-violet-800',
  orange: 'bg-[#FFF7ED] border-orange-200 text-[#b45309]',
  sky: 'bg-sky-50 border-sky-200 text-sky-800',
  gray: 'bg-gray-50 border-gray-200 text-gray-600',
};

function TypeBadge({ p, isEs }) {
  const aff = p.affiliate_code || p.affiliate_name;
  let label; let cls;
  if (p.stage === 'CUSTOMER') { label = isEs ? 'Cliente' : 'Client'; cls = 'bg-emerald-50 text-emerald-600 border-emerald-200'; }
  else if ((p.source || '').toLowerCase().includes('partner') || (p.source || '').toLowerCase().includes('affiliate')) { label = isEs ? 'Afiliado' : 'Affiliate'; cls = 'bg-violet-50 text-violet-600 border-violet-200'; }
  else if (p.stage === 'CALL_BOOKED' || p.stage === 'QUALIFIED') { label = isEs ? 'Potencial 🔥' : 'Hot prospect'; cls = 'bg-orange-50 text-[#d8651a] border-orange-200'; }
  else { label = isEs ? 'Potencial' : 'Prospect'; cls = 'bg-sky-50 text-sky-600 border-sky-200'; }
  return (
    <span className={`inline-flex flex-col items-start gap-0.5 rounded-lg border px-2 py-1 text-[10.5px] font-bold uppercase tracking-wide ${cls}`} data-testid={`type-badge-${p.email}`}>
      {label}
      {aff && <span className="text-[9px] font-semibold normal-case text-gray-400">via {aff}</span>}
    </span>
  );
}

function PersonCard({ p, isEs, delay, onDelete }) {
  const [intent, tone] = aiIntent(p, isEs);
  const bullets = [
    p.scan_completed && (isEs ? `Completó el scan de Index AI${p.index_ai_score != null ? ` (score ${p.index_ai_score})` : ''}` : `Completed the Index AI scan${p.index_ai_score != null ? ` (score ${p.index_ai_score})` : ''}`),
    p.report_downloaded && (isEs ? 'Descargó el reporte completo' : 'Downloaded the full report'),
    p.email_opened && (isEs ? 'Abrió nuestros emails' : 'Opened our emails'),
    p.email_clicked && (isEs ? 'Hizo clic en un CTA del email' : 'Clicked an email CTA'),
    p.landing_page && (isEs ? `Entró por ${p.landing_page}` : `Landed on ${p.landing_page}`),
    (p.affiliate_name || p.affiliate_code) && (isEs ? `Referido por ${p.affiliate_name || p.affiliate_code}` : `Referred by ${p.affiliate_name || p.affiliate_code}`),
  ].filter(Boolean);
  return (
    <Fade delay={delay}>
      <Card className="p-5 h-full flex flex-col hover:shadow-md hover:border-[#F77F11]/40 transition-all duration-300" data-testid={`person-card-${p.email}`}>
        <div className="flex items-start gap-3">
          <span className="w-11 h-11 shrink-0 rounded-xl bg-[#FFF7ED] border border-orange-200 text-[#F77F11] text-[16px] font-bold flex items-center justify-center uppercase">
            {(p.owner || p.restaurant || p.email).slice(0, 1)}
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[14.5px] font-semibold tracking-tight text-gray-900 truncate">{p.owner || p.restaurant || p.email}</p>
            <p className="text-[11.5px] text-gray-400 truncate">{p.restaurant && p.owner ? `${p.restaurant} · ` : ''}{[p.city, p.country].filter(Boolean).join(', ') || p.email}</p>
          </div>
          {p.lead_score != null && (
            <span className="shrink-0 text-center">
              <span className="block text-lg font-bold tabular-nums text-[#F77F11] leading-none">{Math.round(p.lead_score)}</span>
              <span className="text-[9.5px] uppercase tracking-wider text-gray-400 font-medium">score</span>
            </span>
          )}
          <button onClick={() => onDelete(p)} title={isEs ? 'Eliminar registro (informa a Eva)' : 'Delete record (informs Eva)'}
            className="shrink-0 p-1 rounded-md text-gray-300 hover:text-red-600 hover:bg-red-50 transition-colors"
            data-testid={`spotlight-delete-${p.email}`}>
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="mt-3 space-y-1 flex-1">
          {bullets.slice(0, 4).map((b, i) => (
            <p key={i} className="text-[12.5px] text-gray-600 flex items-start gap-1.5"><span className="text-emerald-500 mt-px">✓</span> {b}</p>
          ))}
          {!bullets.length && <p className="text-[12.5px] text-gray-400">{isEs ? 'Dejó su contacto — sin actividad rastreada aún.' : 'Left their contact — no tracked activity yet.'}</p>}
        </div>
        <div className={`mt-3 rounded-lg border px-3 py-2 text-[12px] font-medium ${INTENT_STYLE[tone]}`}>
          <span className="opacity-60 font-semibold">AI:</span> {intent}
        </div>
        <div className="mt-2 flex items-center justify-between">
          <StageBadge stage={p.stage} isEs={isEs} />
          <Link to={`/admin/ai-os/sales?person=${encodeURIComponent(p.email)}`}
            className="text-[11.5px] font-semibold text-[#F77F11] hover:underline" data-testid={`spotlight-open-${p.email}`}>
            {isEs ? 'Ver perfil completo →' : 'Full profile →'}
          </Link>
        </div>
      </Card>
    </Fade>
  );
}

/* Customer Intelligence — the heart of the sales operation.
   Business question: "Who is interacting with Kitxens right now, and how close are they to buying?" */

const MILESTONES = (isEs) => [
  ['scan_completed', isEs ? 'Scan' : 'Scan'],
  ['report_downloaded', isEs ? 'Reporte' : 'Report'],
  ['email_sent', isEs ? 'Email' : 'Email'],
  ['email_opened', isEs ? 'Abierto' : 'Opened'],
  ['email_clicked', 'Click'],
];

function Journey({ p, isEs }) {
  const ms = MILESTONES(isEs);
  return (
    <div className="flex items-center gap-1">
      {ms.map(([k, l]) => (
        <span key={k} title={l}
          className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold border ${
            p[k] ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-gray-50 text-gray-300 border-gray-100'}`}>
          {l}
        </span>
      ))}
    </div>
  );
}

export default function CustomersPage({ embedded = false }) {
  const { lang } = useT();
  const isEs = lang === 'es';
  const { data: d, err, reload, fetchedAt, loading } = useOs2('/customers');
  const { label } = useRangeLabel(isEs);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);

  const deletePerson = async (p) => {
    if (!window.confirm(isEs
      ? `⚠️ ¿Eliminar a ${p.owner || p.restaurant || p.email}?\n\nSe borra de leads y CRM. Eva y el equipo AI serán informados.`
      : `⚠️ Delete ${p.owner || p.restaurant || p.email}?\n\nRemoved from leads & CRM. Eva & AI Team will be informed.`)) return;
    try {
      const r = await authFetch(`/os2/lead/${encodeURIComponent(p.email)}`, { method: 'DELETE' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success(isEs ? 'Registro eliminado ✓ — Eva informada' : 'Record deleted ✓ — Eva informed');
      reload();
    } catch (e) { toast.error(String(e.message)); }
  };

  const rows = useMemo(() => {
    if (!d) return [];
    let r = [...d.people].sort((a, b) => String(b.last_activity || '').localeCompare(String(a.last_activity || '')));
    if (q) {
      const s = q.toLowerCase();
      r = r.filter((x) => [x.email, x.restaurant, x.owner, x.city, x.country, x.affiliate_name]
        .some((v) => (v || '').toLowerCase().includes(s)));
    }
    return r;
  }, [d, q]);
  const PER = 20;
  const pages = Math.max(1, Math.ceil(rows.length / PER));
  const curPage = Math.min(page, pages);
  const pageRows = rows.slice((curPage - 1) * PER, curPage * PER);

  if (err) return <p className="text-sm text-red-600">{err}</p>;
  if (!d) return <LoadingState />;

  const pulse = d.site_pulse;

  return (
    <div data-testid="os2-customers">
      <PageHead
        title={isEs ? 'Inteligencia de Clientes' : 'Customer Intelligence'}
        question={isEs ? '¿Quién interactúa con Kitxens ahora y qué tan cerca está de comprar?' : 'Who is interacting with Kitxens right now, and how close are they to buying?'}
        fresh={embedded ? null : { at: fetchedAt, onReload: reload, loading, isEs }}
        module={embedded ? undefined : 'AI OS'}
      />

      {/* Site pulse strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="customers-pulse">
        <Card className="p-4 flex items-center gap-3">
          <Eye className="w-4 h-4 text-[#F77F11]" aria-hidden="true" />
          <div><p className="text-lg font-semibold tabular-nums leading-none">{pulse.page_views}</p>
            <p className="text-[11px] text-gray-400 mt-1 flex items-center">{isEs ? `Páginas vistas · ${label}` : `Page views · ${label}`}<InfoTip k="page_views" isEs={isEs} /></p></div>
        </Card>
        <Card className="p-4 flex items-center gap-3">
          <Clock3 className="w-4 h-4 text-[#F77F11]" aria-hidden="true" />
          <div><p className="text-lg font-semibold tabular-nums leading-none">{pulse.avg_read_seconds != null ? `${pulse.avg_read_seconds}s` : '—'}</p>
            <p className="text-[11px] text-gray-400 mt-1 flex items-center">{isEs ? 'Tiempo medio de lectura' : 'Avg read time'}<InfoTip k="read_time" isEs={isEs} /></p></div>
        </Card>
        <Card className="p-4 flex items-center gap-3">
          <MessageCircle className="w-4 h-4 text-[#F77F11]" aria-hidden="true" />
          <div><p className="text-lg font-semibold tabular-nums leading-none">{pulse.whatsapp_clicks}</p>
            <p className="text-[11px] text-gray-400 mt-1 flex items-center">{isEs ? `Clics a WhatsApp · ${label}` : `WhatsApp clicks · ${label}`}<InfoTip k="whatsapp_clicks" isEs={isEs} /></p></div>
        </Card>
        <Card className="p-4 flex items-center gap-3">
          <Globe className="w-4 h-4 text-[#F77F11]" aria-hidden="true" />
          <div>
            <p className="text-lg font-semibold tabular-nums leading-none">
              {Object.entries(pulse.languages || {}).map(([l, n]) => `${l.toUpperCase()} ${n}`).join(' · ') || '—'}
            </p>
            <p className="text-[11px] text-gray-400 mt-1">{isEs ? 'Idiomas de visita' : 'Visitor languages'}</p>
          </div>
        </Card>
      </div>

      <AudienceStory people={d.people} pulse={d.site_pulse} isEs={isEs} />

      <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card className="p-4" data-testid="customers-top-pages">
          <p className="text-[11px] tracking-wider uppercase text-gray-400 font-medium mb-2">{isEs ? `Top páginas · ${label}` : `Top pages · ${label}`}</p>
          <div className="flex flex-wrap gap-1.5">
            {(pulse.top_pages || []).length ? pulse.top_pages.map((p) => (
              <span key={p.path} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-50 border border-gray-200 text-[11.5px] text-gray-600">
                {p.path} <b className="tabular-nums text-gray-900">{p.views}</b>
              </span>
            )) : <span className="text-[12px] text-gray-400">{isEs ? 'Sin visitas en este período' : 'No visits in this period'}</span>}
          </div>
        </Card>
        <Card className="p-4" data-testid="customers-intents">
          <p className="text-[11px] tracking-wider uppercase text-gray-400 font-medium mb-2 flex items-center">{isEs ? `Intención de visitantes (Rachel · ${label})` : `Visitor intents (Rachel · ${label})`}<InfoTip k="intents" isEs={isEs} /></p>
          <div className="flex flex-wrap gap-1.5">
            {Object.keys(pulse.visitor_intents || {}).length
              ? Object.entries(pulse.visitor_intents).map(([t, n]) => (
                <span key={t} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-50 border border-orange-200 text-[11.5px] text-[#b45309] capitalize">
                  {t} <b className="tabular-nums">{n}</b>
                </span>
              ))
              : <span className="text-[12px] text-gray-400">{isEs ? 'Sin clasificaciones en este período' : 'No classifications in this period'}</span>}
          </div>
        </Card>
      </div>

      {/* People spotlight */}
      <div className="mt-6 flex items-end justify-between">
        <h2 className="text-[16px] font-semibold tracking-tight text-gray-900">{isEs ? 'Personas destacadas' : 'People spotlight'}</h2>
        <span className="text-[11.5px] text-gray-400">{isEs ? 'ordenadas por cercanía a la compra' : 'sorted by closeness to buying'}</span>
      </div>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="customers-spotlight">
        {[...d.people]
          .sort((a, b) => (({ CUSTOMER: 4, CALL_BOOKED: 3, QUALIFIED: 2 }[b.stage] || 0) * 100 + (b.lead_score || 0))
            - (({ CUSTOMER: 4, CALL_BOOKED: 3, QUALIFIED: 2 }[a.stage] || 0) * 100 + (a.lead_score || 0)))
          .slice(0, 6)
          .map((p, i) => <PersonCard key={p.email} p={p} isEs={isEs} delay={i * 0.06} onDelete={deletePerson} />)}
      </div>

      {/* People table */}
      <Card className="mt-6 overflow-hidden">
        <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-gray-200">
          <div className="relative flex-1 min-w-[220px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300" aria-hidden="true" />
            <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }}
              placeholder={isEs ? 'Buscar contacto…' : 'Search contact…'}
              className="w-full h-9 pl-9 pr-3 rounded-lg border border-gray-200 bg-white text-[13px] focus:outline-none focus:ring-2 focus:ring-[#F77F11]/40"
              data-testid="customers-search" />
          </div>
          <span className="ml-auto text-[12px] text-gray-400 tabular-nums">{rows.length} {isEs ? 'contactos identificados' : 'identified contacts'} · {isEs ? 'pág.' : 'page'} {curPage}/{pages}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse" data-testid="customers-table">
            <thead>
              <tr>
                <Th className="sticky left-0 z-20 bg-gray-50 shadow-[4px_0_8px_-4px_rgba(0,0,0,0.1)]">{isEs ? 'Contacto' : 'Contact'}</Th>
                <Th>{isEs ? 'Tipo' : 'Type'}</Th>
                <Th help="stage" isEs={isEs} className="text-center">{isEs ? 'Etapa' : 'Stage'}</Th>
                <Th help="lead_score" isEs={isEs} className="text-center">Lead score</Th>
                <Th help="journey" isEs={isEs}>{isEs ? 'Recorrido' : 'Journey'}</Th>
                <Th>{isEs ? 'Ciudad / País' : 'City / Country'}</Th>
                <Th>{isEs ? 'Idioma' : 'Lang'}</Th>
                <Th help="source" isEs={isEs}>{isEs ? 'Fuente' : 'Source'}</Th>
                <Th help="affiliate" isEs={isEs}>{isEs ? 'Afiliado' : 'Affiliate'}</Th>
                <Th>Landing</Th>
                <Th help="agent" isEs={isEs}>{isEs ? 'Agente' : 'Agent'}</Th>
                <Th help="last_activity" isEs={isEs}>{isEs ? 'Últ. actividad' : 'Last activity'}</Th>
                <Th help="next_action" isEs={isEs}>{isEs ? 'Próxima acción' : 'Next action'}</Th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((p) => (
                <tr key={p.email} className="hover:bg-orange-50/40 transition-colors" data-testid={`customers-row-${p.email}`}>
                  <Td className="sticky left-0 bg-white z-10 max-w-[220px] shadow-[4px_0_8px_-4px_rgba(0,0,0,0.08)]">
                    <Link to={`/admin/ai-os/sales?person=${encodeURIComponent(p.email)}`} className="block group">
                      <p className="font-semibold text-gray-900 truncate group-hover:text-[#F77F11] transition-colors">{p.owner || p.restaurant || p.email}</p>
                      <p className="text-[11.5px] text-gray-400 truncate">{p.email}</p>
                    </Link>
                  </Td>
                  <Td><TypeBadge p={p} isEs={isEs} /></Td>
                  <Td className="text-center align-middle"><StageBadge stage={p.stage} isEs={isEs} /></Td>
                  <Td className="tabular-nums font-semibold text-center align-middle">{p.lead_score != null ? Math.round(p.lead_score) : '—'}</Td>
                  <Td><Journey p={p} isEs={isEs} /></Td>
                  <Td>{[p.city, p.country].filter(Boolean).join(', ') || '—'}</Td>
                  <Td className="uppercase text-[11.5px]">{p.language || '—'}</Td>
                  <Td><span className="text-[11.5px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">{p.source || '—'}</span></Td>
                  <Td>{p.affiliate_name || p.affiliate_code || '—'}</Td>
                  <Td className="max-w-[140px] truncate">{p.landing_page || '—'}</Td>
                  <Td>{p.agent}</Td>
                  <Td className="text-gray-400">{fmtDateTime(p.last_activity)}</Td>
                  <Td className="max-w-[190px] truncate text-[#b45309]">{p.next_action}</Td>
                </tr>
              ))}
              {!rows.length && (
                <tr><Td className="text-center text-gray-400 py-8" colSpan={13}>{isEs ? 'Sin resultados' : 'No results'}</Td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="pb-3"><Pager page={curPage} pages={pages} setPage={setPage} testid="customers-pager" /></div>
      </Card>
      <p className="mt-2 text-[11.5px] text-gray-400">
        {isEs
          ? 'Se muestran visitantes identificados (dejaron email vía scan, formulario o afiliado). Los anónimos aparecen agregados en el pulso del sitio.'
          : 'Showing identified visitors (left an email via scan, form or affiliate). Anonymous visitors are aggregated in the site pulse.'}
      </p>
    </div>
  );
}
